// « Pièces utilisées » dans le formulaire d'opération.
//
// LA QUESTION À LAQUELLE CE BLOC RÉPOND. Le mécanicien saisit un changement de
// plaquettes. Avant, il devait ouvrir l'écran Stock dans un autre onglet pour
// savoir s'il en restait — et la plupart du temps il ne le faisait pas. Le
// stock affiché ici est celui du magasin, à la ligne près, au moment où la
// question se pose.
//
// CE QUI EST PROPOSÉ EN PREMIER : les références DE CE CAMION. C'est la fiche
// technique du véhicule qui dit quelles plaquettes il consomme ; l'atelier n'a
// pas à retrouver la référence dans un catalogue. Les autres articles restent
// accessibles juste en dessous.
//
// LE STOCK DESCEND CÔTÉ SERVEUR, jamais ici. On envoie la liste avec
// l'opération, et `stock.deplacer()` fait le mouvement dans la même
// transaction — tout ou rien. Décrémenter depuis l'écran laisserait le stock
// baissé si l'enregistrement de l'opération échouait ensuite.
import { useCallback, useEffect, useState } from "react";

import api from "../api";
import { libelleUnite } from "../unites";
import { formaterEuros } from "../format";

function euros(v) {
  return v === null || v === undefined ? "—" : String(v);
}

export default function PiecesConsommees({ vehiculeId, lignes, onChange }) {
  const [articles, setArticles] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [erreur, setErreur] = useState("");

  const charger = useCallback(() => {
    Promise.all([
      api.get("/articles"),
      api.get(`/vehicules/${vehiculeId}/stock-disponible`),
    ])
      .then(([tous, dispo]) => {
        setArticles(tous.data);
        // On ne garde que les références qui ONT un article au magasin : une
        // ligne « pas au stock » n'est pas une pièce qu'on peut sortir.
        setSuggestions(dispo.data.filter((d) => d.article_id));
      })
      .catch(() =>
        // Le bloc disparaît plutôt que d'afficher une liste vide trompeuse :
        // « aucun article » et « on n'a pas pu charger » ne se ressemblent pas.
        setErreur("Stock indisponible pour le moment.")
      );
  }, [vehiculeId]);

  useEffect(charger, [charger]);

  const parId = (id) => articles.find((a) => String(a.id) === String(id));

  const ajouter = (article_id, quantite) => {
    if (lignes.some((l) => String(l.article_id) === String(article_id))) return;
    onChange([...lignes, { article_id: String(article_id), quantite: String(quantite ?? 1) }]);
  };

  const modifier = (i, champ, valeur) => {
    const copie = [...lignes];
    copie[i] = { ...copie[i], [champ]: valeur };
    onChange(copie);
  };

  const retirer = (i) => onChange(lignes.filter((_, j) => j !== i));

  if (erreur) return <p className="sous-info">{erreur}</p>;
  if (articles.length === 0) {
    return (
      <p className="sous-info">
        Aucun article au magasin. Ajoutez-en depuis l’écran <strong>Stock</strong>
        {" "}pour pouvoir les décompter ici.
      </p>
    );
  }

  return (
    <>
      {suggestions.length > 0 && (
        <div className="pieces-suggestions">
          <span className="champ-label">Références de ce véhicule</span>
          <div className="cartes-actions">
            {suggestions.map((s) => {
              const deja = lignes.some(
                (l) => String(l.article_id) === String(s.article_id)
              );
              return (
                <button
                  key={s.article_id}
                  type="button"
                  className="chip"
                  disabled={deja}
                  onClick={() => ajouter(s.article_id, s.quantite_habituelle || 1)}
                >
                  {s.designation}
                  {" · "}
                  {/* LE CHIFFRE QUI COMPTE : ce qu'il reste en magasin. */}
                  <strong>
                    {euros(s.en_stock)} {libelleUnite(s.unite, s.en_stock)}
                  </strong>
                  {s.suffisant === false && " · à commander"}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {lignes.length > 0 && (
        <table className="tableau">
          <thead>
            <tr>
              <th>Article</th>
              <th>En magasin</th>
              <th>Quantité utilisée</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {lignes.map((l, i) => {
              const article = parId(l.article_id);
              const reste = article ? Number(article.quantite) : null;
              const demande = Number(l.quantite);
              // On PRÉVIENT, on ne bloque pas : c'est le serveur qui refuse
              // (409 avec les deux chiffres). Bloquer ici empêcherait de
              // saisir une intervention réelle quand le stock est en retard
              // de saisie — et l'atelier resaisirait ailleurs.
              const manque = reste !== null && demande > reste;
              return (
                <tr key={i} className={manque ? "urgence-rappel" : ""}>
                  <td>
                    <select
                      value={l.article_id}
                      onChange={(e) => modifier(i, "article_id", e.target.value)}
                    >
                      <option value="">— choisir —</option>
                      {articles.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.designation} ({a.reference})
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    {article ? (
                      <>
                        <strong>{euros(article.quantite)}</strong> {libelleUnite(article.unite, article.quantite)}
                      </>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      step="any"
                      value={l.quantite}
                      onChange={(e) => modifier(i, "quantite", e.target.value)}
                    />
                    {manque && (
                      <span className="urgence-retard">
                        {" "}
                        il n’en reste que {euros(article.quantite)}
                      </span>
                    )}
                  </td>
                  <td className="actions-cellule">
                    <button
                      type="button"
                      className="lien-action lien-danger"
                      onClick={() => retirer(i)}
                    >
                      Retirer
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <div className="actions-form">
        <button
          type="button"
          className="btn"
          onClick={() => onChange([...lignes, { article_id: "", quantite: "1" }])}
        >
          + Autre pièce
        </button>
      </div>

      {/* CE QUE LES PIECES COUTENT, AU PRIX DU STOCK (29/09/2026). Affiche
          pour que le mecanicien voie le chiffre -- mais c'est le SERVEUR qui
          le calcule a l'enregistrement, avec le prix de la base : ce montant
          finit dans le cout au km, il ne se fie pas a un calcul d'ecran. */}
      {(() => {
        const chiffrees = lignes
          .filter((l) => l.article_id && Number(l.quantite) > 0)
          .map((l) => ({
            ligne: l,
            article: articles.find((a) => String(a.id) === String(l.article_id)),
          }));
        if (chiffrees.length === 0) return null;
        const total = chiffrees.reduce(
          (t, { ligne, article }) =>
            t + Number(ligne.quantite) * Number(article?.prix_unitaire_ht ?? 0),
          0
        );
        const sansPrix = chiffrees.filter(
          ({ article }) => article && article.prix_unitaire_ht == null
        );
        return (
          <>
            <p className="cout-pieces">
              Coût des pièces : <strong>{formaterEuros(total)}</strong>
              <span className="sous-info"> — calculé au prix du stock</span>
            </p>
            {sansPrix.map(({ article }) => (
              <p key={article.id} className="erreur-champ">
                « {article.designation} » n’a pas de prix dans le stock : compté
                0 €. Renseignez-le dans l’écran Stock.
              </p>
            ))}
          </>
        );
      })()}

      <p className="sous-info">
        Le stock est décompté à l’enregistrement, <strong>tout ou rien</strong>.
        Supprimer l’intervention plus tard rend les pièces au magasin.
      </p>
    </>
  );
}
